/* ============================================================
   audit-compat.js
   Minimal, dependency-free compatibility shims for older iOS
   Safari (audit points #7, #27, #36, #45).

   IMPORTANT: every shim is installed ONLY when the native API is
   missing. On modern browsers this file is a no-op, so it cannot
   regress current traffic — a bug here could only ever affect a
   browser that was already broken.

   Loaded as the first deferred script so the globals exist before
   global.js / custom.js / animations.js run.
   ============================================================ */
(function () {
  'use strict';

  // --- #27 / #36: IntersectionObserver --------------------------------
  // Fallback degrades to "everything is visible": observed elements get
  // their callback fired immediately with isIntersecting = true, so lazy
  // images and scroll-reveal content render instead of staying blank.
  if (typeof window.IntersectionObserver === 'undefined') {
    window.IntersectionObserver = function (callback) {
      this._cb = callback;
      this._targets = [];
    };
    window.IntersectionObserver.prototype.observe = function (el) {
      this._targets.push(el);
      var cb = this._cb;
      // Fire on next tick so calling code finishes wiring up first.
      setTimeout(function () {
        cb(
          [
            {
              isIntersecting: true,
              intersectionRatio: 1,
              target: el,
              boundingClientRect: el.getBoundingClientRect(),
            },
          ],
          this
        );
      }, 0);
    };
    window.IntersectionObserver.prototype.unobserve = function () {};
    window.IntersectionObserver.prototype.disconnect = function () {};
    window.IntersectionObserver.prototype.takeRecords = function () {
      return [];
    };
  }

  // --- #7: ResizeObserver ---------------------------------------------
  // Fallback fires once on observe and again on window resize. Enough to
  // keep slider/layout measurements roughly correct on old iOS.
  if (typeof window.ResizeObserver === 'undefined') {
    window.ResizeObserver = function (callback) {
      this._cb = callback;
      this._targets = [];
      var self = this;
      this._onResize = function () {
        var entries = self._targets.map(function (t) {
          return { target: t, contentRect: t.getBoundingClientRect() };
        });
        if (entries.length) self._cb(entries, self);
      };
      window.addEventListener('resize', this._onResize);
    };
    window.ResizeObserver.prototype.observe = function (el) {
      this._targets.push(el);
      var self = this;
      setTimeout(function () {
        self._cb([{ target: el, contentRect: el.getBoundingClientRect() }], self);
      }, 0);
    };
    window.ResizeObserver.prototype.unobserve = function (el) {
      this._targets = this._targets.filter(function (t) {
        return t !== el;
      });
    };
    window.ResizeObserver.prototype.disconnect = function () {
      this._targets = [];
      window.removeEventListener('resize', this._onResize);
    };
  }

  // --- #45: AbortController -------------------------------------------
  // Fallback provides the shape the code expects. It cannot truly cancel
  // an in-flight fetch on old browsers, but it prevents the ReferenceError
  // that otherwise makes variant fetches throw and hang.
  if (typeof window.AbortController === 'undefined') {
    window.AbortController = function () {
      var listeners = [];
      this.signal = {
        aborted: false,
        addEventListener: function (type, cb) {
          if (type === 'abort') listeners.push(cb);
        },
        removeEventListener: function (type, cb) {
          listeners = listeners.filter(function (l) {
            return l !== cb;
          });
        },
      };
      this._listeners = listeners;
    };
    window.AbortController.prototype.abort = function () {
      this.signal.aborted = true;
      this._listeners.forEach(function (cb) {
        try {
          cb();
        } catch (e) {}
      });
    };
  }
})();
